<template>
  <AdminLayout>
    <div class="p-6 lg:p-8">
      <!-- 페이지 헤더 -->
      <div class="flex items-center gap-3 mb-6">
        <div class="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
          <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
          </svg>
        </div>
        <div>
          <h1 class="text-2xl font-bold text-slate-800">사용자 목록</h1>
          <p class="text-sm text-slate-500">가입된 모든 사용자를 관리합니다</p>
        </div>
      </div>

      <!-- 필터 & 검색 -->
      <div class="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 mb-6">
        <div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <!-- 역할 필터 -->
          <div class="flex gap-2 flex-wrap">
            <button
              v-for="role in roles"
              :key="role.value"
              @click="filterRole = role.value"
              :class="[
                'px-4 py-2 rounded-xl text-sm font-medium transition-all',
                filterRole === role.value
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              ]"
            >
              {{ role.label }}
              <span
                v-if="role.value !== 'all'"
                :class="[
                  'ml-1.5 text-xs px-1.5 py-0.5 rounded-full',
                  filterRole === role.value ? 'bg-white/20' : 'bg-slate-200'
                ]"
              >
                {{ getRoleCount(role.value) }}
              </span>
            </button>
          </div>

          <!-- 정지 필터 -->
          <div class="flex gap-2">
            <button
              @click="filterSuspended = null"
              :class="[
                'px-3 py-2 rounded-lg text-sm font-medium transition-all',
                filterSuspended === null ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'
              ]"
            >전체</button>
            <button
              @click="filterSuspended = true"
              :class="[
                'px-3 py-2 rounded-lg text-sm font-medium transition-all',
                filterSuspended === true ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600'
              ]"
            >정지됨</button>
          </div>

          <!-- 검색 -->
          <div class="flex-1 w-full sm:w-auto sm:min-w-[280px]">
            <div class="relative">
              <svg class="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="이메일 또는 이름으로 검색..."
                class="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <div class="w-12 h-12 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mb-4"></div>
        <p class="text-slate-500">사용자 목록을 불러오는 중...</p>
      </div>

      <!-- 사용자 테이블 -->
      <div v-else class="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="bg-slate-50 border-b border-slate-100">
                <th class="px-4 py-4 text-left text-xs font-semibold text-slate-600">사용자</th>
                <th class="px-4 py-4 text-left text-xs font-semibold text-slate-600">역할</th>
                <th class="px-4 py-4 text-left text-xs font-semibold text-slate-600">상태</th>
                <th class="px-4 py-4 text-left text-xs font-semibold text-slate-600">포인트</th>
                <th class="px-4 py-4 text-left text-xs font-semibold text-slate-600">알림설정</th>
                <th class="px-4 py-4 text-left text-xs font-semibold text-slate-600">마지막 로그인</th>
                <th class="px-4 py-4 text-left text-xs font-semibold text-slate-600">가입일</th>
                <th class="px-4 py-4 text-right text-xs font-semibold text-slate-600">관리</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              <tr
                v-for="user in filteredUsers"
                :key="user.id"
                class="hover:bg-slate-50/50 transition"
                :class="{ 'bg-red-50/50': user.isSuspended }"
              >
                <!-- 사용자 정보 -->
                <td class="px-4 py-3">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        v-if="user.photoURL"
                        :src="user.photoURL"
                        :alt="user.name"
                        class="w-full h-full object-cover"
                      />
                      <div
                        v-else
                        class="w-full h-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center"
                      >
                        <span class="text-white font-semibold text-sm">
                          {{ getInitial(user) }}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p class="font-medium text-slate-800 text-sm">{{ user.name || '이름 없음' }}</p>
                      <p class="text-xs text-slate-500">{{ user.email }}</p>
                    </div>
                  </div>
                </td>

                <!-- 역할 -->
                <td class="px-4 py-3">
                  <span
                    :class="[
                      'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold',
                      getRoleBadgeClass(user.role)
                    ]"
                  >
                    {{ getRoleLabel(user.role) }}
                  </span>
                </td>

                <!-- 상태 -->
                <td class="px-4 py-3">
                  <span
                    v-if="user.isSuspended"
                    class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-600"
                  >
                    정지됨
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-600"
                  >
                    정상
                  </span>
                </td>

                <!-- 포인트 -->
                <td class="px-4 py-3">
                  <div v-if="user.role === 'partner'" class="text-sm">
                    <span class="font-medium text-violet-600">{{ (user.partnerPoints || 0).toLocaleString() }}P</span>
                    <span class="text-xs text-slate-400 block">파트너스</span>
                  </div>
                  <span v-else class="text-sm font-medium text-slate-700">
                    {{ (user.points || 0).toLocaleString() }}P
                  </span>
                </td>

                <!-- 알림설정 -->
                <td class="px-4 py-3">
                  <div class="flex flex-col gap-0.5 text-xs">
                    <span :class="user.notificationSettings?.marketing ? 'text-green-600' : 'text-slate-400'">
                      마케팅: {{ user.notificationSettings?.marketing ? 'ON' : 'OFF' }}
                    </span>
                    <span :class="user.notificationSettings?.notice ? 'text-green-600' : 'text-slate-400'">
                      공지: {{ user.notificationSettings?.notice ? 'ON' : 'OFF' }}
                    </span>
                  </div>
                </td>

                <!-- 마지막 로그인 -->
                <td class="px-4 py-3">
                  <span class="text-xs text-slate-600">{{ formatDateTime(user.lastLoginAt) }}</span>
                </td>

                <!-- 가입일 -->
                <td class="px-4 py-3">
                  <span class="text-xs text-slate-600">{{ formatDate(user.createdAt) }}</span>
                </td>

                <!-- 관리 -->
                <td class="px-4 py-3 text-right">
                  <div class="flex items-center justify-end gap-2">
                    <select
                      v-model="user.role"
                      @change="updateUserRole(user)"
                      class="text-xs px-2 py-1.5 border border-slate-200 rounded-lg focus:border-blue-600 outline-none bg-white cursor-pointer"
                    >
                      <option value="user">일반</option>
                      <option value="partner">파트너</option>
                      <option value="admin">관리자</option>
                    </select>
                    <button
                      v-if="!user.isSuspended"
                      @click="openSuspendModal(user)"
                      class="px-2 py-1.5 text-xs font-medium bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
                    >
                      정지
                    </button>
                    <button
                      v-else
                      @click="handleUnsuspend(user)"
                      class="px-2 py-1.5 text-xs font-medium bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition"
                    >
                      해제
                    </button>
                    <button
                      @click="openDetailModal(user)"
                      class="px-2 py-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition"
                    >
                      상세
                    </button>
                  </div>
                </td>
              </tr>

              <!-- 빈 상태 -->
              <tr v-if="filteredUsers.length === 0">
                <td colspan="8" class="px-6 py-16 text-center">
                  <div class="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                    </svg>
                  </div>
                  <p class="text-slate-500 font-medium">사용자가 없습니다</p>
                  <p class="text-sm text-slate-400 mt-1">검색 조건을 변경해보세요</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 통계 -->
      <div class="mt-6 flex items-center justify-between flex-wrap gap-4">
        <p class="text-sm text-slate-500">
          총 <span class="font-semibold text-slate-700">{{ filteredUsers.length }}</span>명의 사용자
        </p>
        <div class="flex items-center gap-4 text-sm text-slate-500">
          <span class="flex items-center gap-1.5">
            <span class="w-2 h-2 bg-slate-400 rounded-full"></span>
            일반 {{ getRoleCount('user') }}
          </span>
          <span class="flex items-center gap-1.5">
            <span class="w-2 h-2 bg-violet-500 rounded-full"></span>
            파트너 {{ getRoleCount('partner') }}
          </span>
          <span class="flex items-center gap-1.5">
            <span class="w-2 h-2 bg-blue-600 rounded-full"></span>
            관리자 {{ getRoleCount('admin') }}
          </span>
          <span class="flex items-center gap-1.5">
            <span class="w-2 h-2 bg-red-500 rounded-full"></span>
            정지 {{ getSuspendedCount() }}
          </span>
        </div>
      </div>

      <!-- 정지 모달 -->
      <div v-if="showSuspendModal" class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div class="bg-white rounded-2xl w-full max-w-md overflow-hidden">
          <div class="p-6 border-b border-slate-100">
            <h3 class="text-lg font-bold text-slate-800">사용자 정지</h3>
          </div>
          <div class="p-6">
            <p class="text-sm text-slate-600 mb-4">
              <strong>{{ selectedUser?.name || selectedUser?.email }}</strong> 사용자를 정지하시겠습니까?
            </p>
            <div class="mb-4">
              <label class="block text-sm font-medium text-slate-700 mb-2">정지 사유</label>
              <textarea
                v-model="suspendReason"
                rows="3"
                class="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-red-500"
                placeholder="정지 사유를 입력하세요"
              ></textarea>
            </div>
          </div>
          <div class="p-6 border-t border-slate-100 flex gap-3">
            <button
              @click="closeSuspendModal"
              class="flex-1 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition"
            >취소</button>
            <button
              @click="handleSuspend"
              :disabled="!suspendReason.trim()"
              class="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition disabled:bg-red-300"
            >정지하기</button>
          </div>
        </div>
      </div>

      <!-- 상세 모달 -->
      <div v-if="showDetailModal" class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div class="bg-white rounded-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
          <div class="p-6 border-b border-slate-100 flex items-center justify-between">
            <h3 class="text-lg font-bold text-slate-800">사용자 상세 정보</h3>
            <button @click="closeDetailModal" class="p-2 hover:bg-slate-100 rounded-lg">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="p-6" v-if="selectedUser">
            <!-- 프로필 -->
            <div class="flex items-center gap-4 mb-6">
              <div class="w-16 h-16 rounded-full overflow-hidden">
                <img v-if="selectedUser.photoURL" :src="selectedUser.photoURL" class="w-full h-full object-cover" />
                <div v-else class="w-full h-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center">
                  <span class="text-white font-bold text-xl">{{ getInitial(selectedUser) }}</span>
                </div>
              </div>
              <div>
                <p class="text-lg font-bold text-slate-800">{{ selectedUser.name || '이름 없음' }}</p>
                <p class="text-sm text-slate-500">{{ selectedUser.email }}</p>
              </div>
            </div>

            <!-- 정보 목록 -->
            <div class="space-y-4">
              <div class="flex justify-between py-3 border-b border-slate-100">
                <span class="text-sm text-slate-500">역할</span>
                <span class="text-sm font-medium text-slate-800">{{ getRoleLabel(selectedUser.role) }}</span>
              </div>
              <div class="flex justify-between py-3 border-b border-slate-100">
                <span class="text-sm text-slate-500">상태</span>
                <span :class="['text-sm font-medium', selectedUser.isSuspended ? 'text-red-600' : 'text-green-600']">
                  {{ selectedUser.isSuspended ? '정지됨' : '정상' }}
                </span>
              </div>
              <div v-if="selectedUser.isSuspended" class="py-3 border-b border-slate-100">
                <span class="text-sm text-slate-500">정지 사유</span>
                <p class="text-sm text-red-600 mt-1">{{ selectedUser.suspendReason }}</p>
                <p class="text-xs text-slate-400 mt-1">정지일: {{ formatDateTime(selectedUser.suspendedAt) }}</p>
              </div>
              <div class="flex justify-between py-3 border-b border-slate-100">
                <span class="text-sm text-slate-500">포인트</span>
                <div v-if="selectedUser.role === 'partner'" class="text-right">
                  <span class="text-sm font-medium text-violet-600">{{ (selectedUser.partnerPoints || 0).toLocaleString() }}P</span>
                  <span class="text-xs text-slate-400 block">파트너스 포인트</span>
                </div>
                <span v-else class="text-sm font-medium text-slate-800">{{ (selectedUser.points || 0).toLocaleString() }}P</span>
              </div>
              <div class="flex justify-between py-3 border-b border-slate-100">
                <span class="text-sm text-slate-500">마지막 로그인</span>
                <span class="text-sm text-slate-800">{{ formatDateTime(selectedUser.lastLoginAt) }}</span>
              </div>
              <div class="flex justify-between py-3 border-b border-slate-100">
                <span class="text-sm text-slate-500">가입일</span>
                <span class="text-sm text-slate-800">{{ formatDateTime(selectedUser.createdAt) }}</span>
              </div>
              <div class="py-3 border-b border-slate-100">
                <span class="text-sm text-slate-500 block mb-2">알림 설정</span>
                <div class="flex gap-3 text-sm">
                  <span :class="selectedUser.notificationSettings?.marketing ? 'text-green-600' : 'text-slate-400'">
                    마케팅: {{ selectedUser.notificationSettings?.marketing ? 'ON' : 'OFF' }}
                  </span>
                  <span :class="selectedUser.notificationSettings?.notice ? 'text-green-600' : 'text-slate-400'">
                    공지: {{ selectedUser.notificationSettings?.notice ? 'ON' : 'OFF' }}
                  </span>
                  <span :class="selectedUser.notificationSettings?.consultation ? 'text-green-600' : 'text-slate-400'">
                    상담: {{ selectedUser.notificationSettings?.consultation ? 'ON' : 'OFF' }}
                  </span>
                </div>
              </div>
              <div class="flex justify-between py-3">
                <span class="text-sm text-slate-500">UID</span>
                <span class="text-xs text-slate-400 font-mono">{{ selectedUser.id }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, getDocs, doc, getDoc, updateDoc, orderBy, query } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'

export default {
  name: 'UserListView',
  components: {
    AdminLayout
  },
  setup() {
    const users = ref([])
    const loading = ref(true)
    const filterRole = ref('all')
    const filterSuspended = ref(null)
    const searchQuery = ref('')

    // 모달 상태
    const showSuspendModal = ref(false)
    const showDetailModal = ref(false)
    const selectedUser = ref(null)
    const suspendReason = ref('')

    const roles = [
      { value: 'all', label: '전체' },
      { value: 'user', label: '일반' },
      { value: 'partner', label: '파트너' },
      { value: 'admin', label: '관리자' }
    ]

    const getRoleCount = (role) => {
      return users.value.filter(u => u.role === role).length
    }

    const getSuspendedCount = () => {
      return users.value.filter(u => u.isSuspended).length
    }

    const filteredUsers = computed(() => {
      return users.value.filter((user) => {
        // 역할 필터
        if (filterRole.value !== 'all' && user.role !== filterRole.value) {
          return false
        }

        // 정지 필터
        if (filterSuspended.value !== null) {
          if (filterSuspended.value && !user.isSuspended) return false
          if (!filterSuspended.value && user.isSuspended) return false
        }

        // 검색 필터
        if (searchQuery.value) {
          const q = searchQuery.value.toLowerCase()
          const matchEmail = user.email?.toLowerCase().includes(q)
          const matchName = user.name?.toLowerCase().includes(q)
          if (!matchEmail && !matchName) {
            return false
          }
        }

        return true
      })
    })

    const fetchUsers = async () => {
      loading.value = true

      try {
        const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'))
        const snapshot = await getDocs(q)

        const userList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))

        // 파트너 사용자의 파트너스 포인트 조회
        for (const user of userList) {
          if (user.role === 'partner') {
            try {
              const pointDoc = await getDoc(doc(db, 'partnerPoints', user.id))
              if (pointDoc.exists()) {
                user.partnerPoints = pointDoc.data().balance || 0
              } else {
                user.partnerPoints = 0
              }
            } catch {
              user.partnerPoints = 0
            }
          }
        }

        users.value = userList
      } catch (error) {
        console.error('사용자 목록 로드 실패:', error)
      } finally {
        loading.value = false
      }
    }

    const updateUserRole = async (user) => {
      try {
        await updateDoc(doc(db, 'users', user.id), {
          role: user.role,
          updatedAt: new Date().toISOString()
        })
      } catch (error) {
        console.error('역할 업데이트 실패:', error)
        alert('역할 변경에 실패했습니다.')
        fetchUsers()
      }
    }

    // 정지 모달
    const openSuspendModal = (user) => {
      selectedUser.value = user
      suspendReason.value = ''
      showSuspendModal.value = true
    }

    const closeSuspendModal = () => {
      showSuspendModal.value = false
      selectedUser.value = null
      suspendReason.value = ''
    }

    const handleSuspend = async () => {
      if (!selectedUser.value || !suspendReason.value.trim()) return

      try {
        await updateDoc(doc(db, 'users', selectedUser.value.id), {
          isSuspended: true,
          suspendReason: suspendReason.value.trim(),
          suspendedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })

        // 로컬 상태 업데이트
        const idx = users.value.findIndex(u => u.id === selectedUser.value.id)
        if (idx !== -1) {
          users.value[idx].isSuspended = true
          users.value[idx].suspendReason = suspendReason.value.trim()
          users.value[idx].suspendedAt = new Date().toISOString()
        }

        closeSuspendModal()
      } catch (error) {
        console.error('사용자 정지 실패:', error)
        alert('정지 처리에 실패했습니다.')
      }
    }

    const handleUnsuspend = async (user) => {
      if (!confirm(`${user.name || user.email} 사용자의 정지를 해제하시겠습니까?`)) return

      try {
        await updateDoc(doc(db, 'users', user.id), {
          isSuspended: false,
          suspendReason: null,
          suspendedAt: null,
          updatedAt: new Date().toISOString()
        })

        // 로컬 상태 업데이트
        const idx = users.value.findIndex(u => u.id === user.id)
        if (idx !== -1) {
          users.value[idx].isSuspended = false
          users.value[idx].suspendReason = null
          users.value[idx].suspendedAt = null
        }
      } catch (error) {
        console.error('정지 해제 실패:', error)
        alert('정지 해제에 실패했습니다.')
      }
    }

    // 상세 모달
    const openDetailModal = (user) => {
      selectedUser.value = user
      showDetailModal.value = true
    }

    const closeDetailModal = () => {
      showDetailModal.value = false
      selectedUser.value = null
    }

    const getInitial = (user) => {
      const name = user.name || user.email || ''
      return name.charAt(0).toUpperCase()
    }

    const getRoleLabel = (role) => {
      const labels = {
        user: '일반 사용자',
        partner: '파트너',
        admin: '관리자'
      }
      return labels[role] || role
    }

    const getRoleBadgeClass = (role) => {
      const classes = {
        user: 'bg-slate-100 text-slate-600',
        partner: 'bg-violet-100 text-violet-600',
        admin: 'bg-blue-600/10 text-blue-600'
      }
      return classes[role] || 'bg-slate-100 text-slate-600'
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('ko-KR', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
      })
    }

    const formatDateTime = (dateStr) => {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    onMounted(() => {
      fetchUsers()
    })

    return {
      users,
      loading,
      filterRole,
      filterSuspended,
      searchQuery,
      roles,
      filteredUsers,
      getRoleCount,
      getSuspendedCount,
      updateUserRole,
      getInitial,
      getRoleLabel,
      getRoleBadgeClass,
      formatDate,
      formatDateTime,
      // 모달
      showSuspendModal,
      showDetailModal,
      selectedUser,
      suspendReason,
      openSuspendModal,
      closeSuspendModal,
      handleSuspend,
      handleUnsuspend,
      openDetailModal,
      closeDetailModal
    }
  }
}
</script>
